'use client';

import type { FC, ReactNode } from 'react';
import { List, Spin, Tag, Tooltip, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { GetKmsDocumentStatusResponse } from '~/app/services/kms/kms-document.types';
import { formatConfidencePercent } from '~/app/services/kms/kms-document.utils';
import { useKmsAssetLinkCandidates } from './hooks/useKmsAssetLinkCandidates';

const { Text } = Typography;

export interface KmsAssetLinkCandidatesListProps {
  document: GetKmsDocumentStatusResponse;
  organizationId: string | null;
}

export const KmsAssetLinkCandidatesList: FC<
  KmsAssetLinkCandidatesListProps
> = ({ document, organizationId }) => {
  const { t } = useTranslation('kms');
  const { rows, isLoading, isEmpty } = useKmsAssetLinkCandidates({
    document,
    organizationId,
  });

  if (isEmpty) {
    return (
      <Text type="secondary">{t('detail.assetLink.noAssetCandidates')}</Text>
    );
  }

  if (isLoading) {
    return (
      <FlexCenter>
        <Spin size="small" />
      </FlexCenter>
    );
  }

  return (
    <List
      size="small"
      bordered
      dataSource={rows}
      header={<Text strong>{t('detail.assetLink.candidatesTitle')}</Text>}
      renderItem={(row) => (
        <List.Item
          actions={[
            <Tag key="score" color="blue">
              {formatConfidencePercent(row.score)}
            </Tag>,
          ]}
        >
          <List.Item.Meta
            title={
              <Tooltip
                title={
                  row.reason
                    ? t('detail.assetLink.candidateMatchReason', {
                        reason: row.reason,
                      })
                    : (row.assetName ?? undefined)
                }
              >
                <Link to={`/assets/${row.assetId}`}>{row.serialLabel}</Link>
              </Tooltip>
            }
            description={
              row.assetName && row.serialLabel !== row.assetName ? (
                <Text type="secondary">{row.assetName}</Text>
              ) : null
            }
          />
        </List.Item>
      )}
    />
  );
};

function FlexCenter({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
      {children}
    </div>
  );
}
